import React, { forwardRef, useState } from "react";
import ReactFlagsSelect from "react-flags-select";
import styled from "styled-components";
import { bool, string, node } from "prop-types";
import { color, fontSize, font } from "./utils";
import { useSolJourneySelector } from "@redux/reducers/appConfig";
import { useUserProfileSelector } from "@redux/reducers/deal";
import { useTranslation, Trans } from "@lib/i18n";
import Typography from "./Typography";
import { useDetectDevice } from "@hooks/useDetectDevice";

import { COUNTRIES, COUNTRY_CODES } from "@shared/constants";

const StyledReactFlagsSelect = styled(ReactFlagsSelect)`
  padding-bottom: 0 !important;
  margin-right: 0.5rem !important;
  margin: -0.5rem 0;
  font-size: 1rem !important;
  min-width: 8rem;

  & button {
    border: none;
    border-right: 2px solid ${color("grey")};
    border-radius: 0px;
    transform: none;
    font-size: 0.875rem !important;
    &[aria-expanded="false"]:after,
    &[aria-expanded="true"]:after {
      border: solid black;
      border-width: 0 2px 2px 0;
      display: inline-block;
      padding: 2px;
      transform: rotate(45deg);
    }
  }

  & ul {
    font-size: ${fontSize("sm")} !important;
  }
`;

const StyledInput = styled.input`
  border: none;
  font-size: ${fontSize("sm")};
  font-family: ${font("ubuntu")};
  outline: none;
  flex: 1;
  background: ${({ disabled }) => (disabled ? color("grey") : "none")};
  padding: 0.5rem;
`;

const StyledLabel = styled.label`
  display: block;
  cursor: text;
  margin-top: 2rem;
  width: ${({ fullWidth }) => fullWidth && "100%"};
`;

const StyledInputContainer = styled.div`
  align-items: center;
  border: 2px solid ${color("grey")};
  background: ${({ disabled }) => (disabled ? color("grey") : color("white"))};
  border-radius: 5px;
  display: flex;
  font-size: 1rem;
  letter-spacing: 0.25px;
  margin-top: 0.5rem;
`;

const StyledTypography = styled(Typography)`
  font-size: ${fontSize("xs")};
  font-family: inherit;
  color: ${color("green")};
  margin: 0.25rem 0.5rem 0;
`;

const TelephoneField = forwardRef(
  (
    {
      required,
      className,
      fullWidth,
      label,
      disabled,
      maxLength,
      name,
      ...props
    },
    ref
  ) => {
    const userProfile = useUserProfileSelector();
    const { isMobile } = useDetectDevice();

    const { countryCode } = useSolJourneySelector();
    const countries = Object.values(COUNTRIES);
    const countryCodes = Object.values(COUNTRY_CODES);
    const { t } = useTranslation();

    const isCountryCodeIncludedInPhoneNumber = () => {
      return countryCodes.some(code => userProfile.phone?.includes(code));
    };

    const initialCountryCode = countryCode => {
      if (isCountryCodeIncludedInPhoneNumber()) {
        return countryCodes.find(code => userProfile.phone.includes(code));
      }
      return countryCode;
    };

    const getKeyByValue = (object, value) => {
      return Object.keys(object).find(key => object[key] === value);
    };

    const [selected, setSelected] = useState(
      getKeyByValue(COUNTRY_CODES, initialCountryCode(countryCode))
    );

    const [countryCodeNumber, setCountryCodeNumber] = useState(
      initialCountryCode(countryCode)
    );

    const initialNumberValue = isCountryCodeIncludedInPhoneNumber()
      ? userProfile.phone.slice(initialCountryCode(countryCode).length)
      : "";

    const [phoneNumberInput, setPhoneNumberInput] = useState(
      countryCodeNumber + initialNumberValue
    );

    const handleCountryCodeNumber = (codeCode, country) => {
      setCountryCodeNumber(codeCode);
      setSelected(country);
      setPhoneNumberInput(codeCode);
    };

    const handlePhoneNumberChange = e => {
      setPhoneNumberInput(e.target.value);
    };

    const handleCountryCodeChange = code => {
      for (const key in COUNTRIES) {
        if (COUNTRIES[key] === code) {
          handleCountryCodeNumber(COUNTRY_CODES[key], COUNTRIES[key]);
        }
      }
    };

    return (
      <>
        <StyledLabel className={className} fullWidth={fullWidth}>
          {label}
          {required && "*"}
          <StyledInputContainer disabled={disabled}>
            <StyledReactFlagsSelect
              selected={selected}
              onSelect={handleCountryCodeChange}
              countries={countries}
              searchable="false"
              optionsSize={16}
              selectedSize={16}
            />
            <StyledInput
              type={"text"}
              required={required}
              ref={ref}
              disabled={disabled}
              maxLength={maxLength}
              name={name}
              onChange={e => handlePhoneNumberChange(e)}
              value={phoneNumberInput}
              {...props}
            />
          </StyledInputContainer>
          {!isCountryCodeIncludedInPhoneNumber() && (
            <StyledTypography>
              <Trans
                i18nKey={t("myDetails.formatPhoneNumber")}
                components={{ br: isMobile ? <></> : <br /> }}
              />
            </StyledTypography>
          )}
        </StyledLabel>
      </>
    );
  }
);

TelephoneField.displayName = "TelephoneField";

TelephoneField.propTypes = {
  required: bool,
  disabled: bool,
  maxLength: string,
  name: string,
  className: string,
  fullWidth: bool,
  label: node,
};

export default TelephoneField;
